import type { TelemetryEvent } from "./events";

/**
 * Export helpers — turn the event log into JSON and CSV files for analysis.
 *
 * CSV columns are the union of all keys seen across events, with object/array
 * values JSON-stringified into a single cell. This keeps every event type in
 * one flat table without losing detail.
 */

export function eventsToJson(events: TelemetryEvent[]): string {
  return JSON.stringify(events, null, 2);
}

export function eventsToCsv(events: TelemetryEvent[]): string {
  if (events.length === 0) return "";

  // Stable, human-friendly leading columns; the rest follow alphabetically.
  const preferred = [
    "tsIso",
    "ts",
    "type",
    "family",
    "child",
    "storyId",
    "mode",
    "sessionId",
    "eventId",
  ];

  const keySet = new Set<string>();
  for (const ev of events) {
    for (const k of Object.keys(ev)) keySet.add(k);
  }
  const rest = [...keySet].filter((k) => !preferred.includes(k)).sort();
  const columns = [...preferred.filter((k) => keySet.has(k)), ...rest];

  const rows = events.map((ev) =>
    columns
      .map((col) => csvCell((ev as unknown as Record<string, unknown>)[col]))
      .join(",")
  );

  return [columns.join(","), ...rows].join("\r\n");
}

function csvCell(value: unknown): string {
  if (value === undefined || value === null) return "";
  const str =
    typeof value === "object" ? JSON.stringify(value) : String(value);
  // Escape per RFC 4180.
  if (/[",\r\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/** Trigger a browser download of a string as a file. */
export function downloadText(
  filename: string,
  text: string,
  mime: string
): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after a tick so the download can start.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadJson(events: TelemetryEvent[], baseName: string): void {
  downloadText(`${baseName}.json`, eventsToJson(events), "application/json");
}

export function downloadCsv(events: TelemetryEvent[], baseName: string): void {
  downloadText(`${baseName}.csv`, eventsToCsv(events), "text/csv");
}
